import { Dispatch, MutableRefObject, SetStateAction, useCallback } from "react";
import { useSelector } from "react-redux";

import { EMPTY_HOVER_LEGEND, HoverLegendState } from "atoms/interaction";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import { MCDANIEL_SEARCH_FOLDER_NAME } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";
import { toPdenEnum } from "utils/toPdenEnum";

import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import { UserEventContext } from "api/userEvents";

import {
  IGroupByListColumn,
  LegendItemModel,
  PerformanceDateRange,
  SHAPEFILE_PROPERTY_PREFIX
} from "models";
import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { stripTileQualifications } from "components/groupBy/helpers";
import { isForecastQualifiedProperty } from "components/groupBy/helpers/qualifyForecastProperty";
import { DefaultBinSettings } from "components/user-settings/models";

import { GroupByFilterDependencies, GroupBySelection } from "../types";
import { updateGroupBy } from "../util";
import { getStoredPerformanceDateRangeFromSession } from "../util/getStoredPerformanceDateRangeFromSession";
import { setStoredPerformanceDateRangeToSession } from "../util/setStoredPerformanceDateRangeToSession";

export interface FocusSelectionControllerProps {
  deps: GroupByFilterDependencies;
  entityKind: EntityKind;
  isForecastToggleOn: boolean;
  setIsForecastToggleOn: Dispatch<SetStateAction<boolean>>;
  selectedForecastFolder: string;
  setSelectedForecastFolder: Dispatch<SetStateAction<string>>;
  setCumPlusForecast: (value: boolean) => void;
  loadShapefileColumn: (property: string) => Promise<Nullable<IGroupByListColumn>>;
  sendGlobalGroupByChangedEvent: (context?: UserEventContext) => Promise<void>;
  scheduleHoverWrite: (next: HoverLegendState) => void;
  updateSelectedLegendItems: (items: LegendItemModel[]) => void;
  localPerformanceDateRangeRef: MutableRefObject<
    Maybe<Record<string, PerformanceDateRange>>
  >;
  localBinsRef: MutableRefObject<Nullable<Record<string, mBinSize>>>;
  updateField: (
    fieldGb: GroupByFilterDependencies["groupBy"],
    updateBinsOnly?: boolean
  ) => void;
  defaultBinSettings: DefaultBinSettings;
  setFocusSearch: Dispatch<SetStateAction<string>>;
}

export interface FocusSelectionControllerResult {
  updateGroupByFromSelection: (selection: GroupBySelection) => Promise<void>;
  handlePresetSelection: (selection: GroupBySelection) => void;
  handleRecentFocusItemClick: (arg: { key: string }) => void;
  handleGroupByChange: (
    groupBy: IGroupByListColumn,
    isForecastToggleOn: boolean,
    selectedForecastFolder: string,
    selectedForecastFolderName: string,
    selectedPdenSource: string,
    performanceDateRange?: PerformanceDateRange
  ) => void;
}

// EVA-6412 P1 thin-controller follow-up — owns the focus/groupBy SELECTION
// handlers (updateGroupByFromSelection/handlePresetSelection/
// handleRecentFocusItemClick/handleGroupByChange), lifted verbatim out of
// useGroupByFilterController. Consumes the resolved `deps` (never entityKind
// re-derivation) plus the specific forecast/session/bin/legend primitives it
// needs — never a whole sibling domain hook's result object, to match the
// existing useLegendController/useBinSettingsController prop-threading
// convention (../types.ts GroupByFilterDependencies + explicit scalar props).
export const useFocusSelectionController = ({
  deps,
  entityKind,
  isForecastToggleOn,
  setIsForecastToggleOn,
  selectedForecastFolder,
  setSelectedForecastFolder,
  setCumPlusForecast,
  loadShapefileColumn,
  sendGlobalGroupByChangedEvent,
  scheduleHoverWrite,
  updateSelectedLegendItems,
  localPerformanceDateRangeRef,
  localBinsRef,
  updateField,
  defaultBinSettings,
  setFocusSearch
}: FocusSelectionControllerProps): FocusSelectionControllerResult => {
  const pdenDataSourceSetting = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting
  );
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;

  const updateGroupByFromSelection = useCallback(
    async ({ property, dataType, options }: GroupBySelection) => {
      // A tile can carry its date range and its production source in the property string, but
      // the focus is committed with both cut back out. Everything this callback stores or looks
      // up has to be keyed the stripped way, or a tile click and the same field picked by hand
      // write two entries for one field and neither side finds what the other left.
      const fieldKey = stripTileQualifications(property);
      let columnsDict = deps.columnsDictionary;
      let effectiveDataType = dataType;
      if (property.startsWith(SHAPEFILE_PROPERTY_PREFIX) && !columnsDict[property]) {
        const loaded = await loadShapefileColumn(property);
        if (!loaded) {
          return;
        }
        columnsDict = { ...columnsDict, [property]: loaded };
        effectiveDataType = effectiveDataType || loaded.dataType;
      }

      const nextForecastToggleOn = options?.isForecastToggleOn ?? isForecastToggleOn;
      const nextForecastFolderName =
        options?.selectedForecastFolderName ?? selectedForecastFolder;
      // A tile that names no production source is meant to run on the organization's
      // setting, which is also the source it was measured against when it was saved.
      // Falling back to the committed focus instead would lend it whatever source the tab
      // is already showing, so a tile whose pin the user cleared would keep answering from
      // the pinned source until the tab was reloaded.
      //
      // Read with ?? and not for truth, because gDC is 0.
      const nextPdenSource =
        options?.pdenSource ?? toPdenEnum(pdenDataSourceSetting.source);

      if (options?.isForecastToggleOn !== undefined) {
        setIsForecastToggleOn(options.isForecastToggleOn);
      }
      // Test against undefined, not truthiness. A caller that means "no forecast source"
      // sends an empty name, and the panel state has to take it: the Forecast-category
      // title appends the folder whether or not the toggle is on, so a skipped write
      // leaves the previous field's source on the header.
      if (options?.selectedForecastFolderName !== undefined) {
        setSelectedForecastFolder(options.selectedForecastFolderName);
      }

      sendGlobalGroupByChangedEvent({ groupBy: fieldKey, entityKind, dataType });
      updateSelectedLegendItems([]);
      scheduleHoverWrite(EMPTY_HOVER_LEGEND);

      if (options?.performanceDateRange) {
        const freshDateRanges = getStoredPerformanceDateRangeFromSession();
        const mergedDateRanges = {
          ...freshDateRanges,
          [fieldKey]: options.performanceDateRange
        };
        localPerformanceDateRangeRef.current = mergedDateRanges;
        setStoredPerformanceDateRangeToSession(mergedDateRanges);
      }

      updateGroupBy(
        property,
        columnsDict,
        updateField,
        Object.prototype.hasOwnProperty.call(localBinsRef.current, fieldKey)
          ? localBinsRef.current[fieldKey]
          : null,
        nextForecastFolderName,
        options?.fullContextTitleWithNormalization,
        options?.partialContextTitle,
        nextForecastToggleOn,
        nextPdenSource,
        entityKind,
        defaultBinSettings,
        effectiveDataType,
        options?.performanceDateRange ?? localPerformanceDateRangeRef.current[fieldKey],
        maxBinDefaultsEnabled
      );

      setFocusSearch("");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deps,
      entityKind,
      defaultBinSettings,
      loadShapefileColumn,
      updateField,
      isForecastToggleOn,
      selectedForecastFolder,
      pdenDataSourceSetting,
      maxBinDefaultsEnabled
    ]
  );

  const handlePresetSelection = useCallback(
    (selection: GroupBySelection) => {
      updateGroupByFromSelection(selection);
    },
    [updateGroupByFromSelection]
  );

  const handleRecentFocusItemClick = useCallback(
    ({ key }: { key: string }) => {
      if (Number(key) < 0) return;
      const keyData = JSON.parse(key);
      if (keyData?.property && keyData?.dataType) {
        handlePresetSelection({
          property: keyData.property,
          dataType: keyData.dataType,
          options: {
            isForecastToggleOn: keyData.isForecastToggleOn,
            selectedForecastFolderName: keyData.selectedForecastFolderName,
            partialContextTitle: keyData.partialContextTitle,
            fullContextTitleWithNormalization: keyData.fullContextTitleWithNormalization,
            pdenSource: keyData.pdenSource
          }
        });
      }
    },
    [handlePresetSelection]
  );

  const handleGroupByChange = useCallback(
    (
      groupBy: IGroupByListColumn,
      isForecastToggleOnArg: boolean,
      _selectedForecastFolder: string,
      selectedForecastFolderName: string,
      selectedPdenSource: string,
      performanceDateRange?: PerformanceDateRange
    ) => {
      setCumPlusForecast(isForecastToggleOnArg);
      setIsForecastToggleOn(isForecastToggleOnArg);
      setSelectedForecastFolder(selectedForecastFolderName);
      // A Performance field is labelled with the forecast source only when its property
      // says a forecast was used. Left alone under EVA Research, which stores its
      // forecast fields with a bare property on purpose.
      const labelForecastToggleOn =
        isForecastToggleOnArg &&
        groupBy.group === "Performance" &&
        selectedForecastFolderName !== EVA_RESEARCH_NAME &&
        selectedForecastFolderName !== MCDANIEL_SEARCH_FOLDER_NAME
          ? isForecastQualifiedProperty(groupBy.property)
          : isForecastToggleOnArg;
      const labelContext = getColumnLabelWithContext(
        groupBy,
        deps.useNormalizeBy,
        deps.normalizeBy,
        labelForecastToggleOn,
        selectedForecastFolderName,
        entityKind,
        performanceDateRange
      );
      updateGroupByFromSelection({
        property: groupBy.property,
        dataType: groupBy.dataType,
        options: {
          isForecastToggleOn: isForecastToggleOnArg,
          selectedForecastFolderName,
          partialContextTitle: labelContext.partialContextTitle,
          fullContextTitleWithNormalization:
            labelContext.fullContextTitleWithNormalization,
          pdenSource: selectedPdenSource,
          performanceDateRange
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateGroupByFromSelection, deps.useNormalizeBy, deps.normalizeBy, entityKind]
  );

  return {
    updateGroupByFromSelection,
    handlePresetSelection,
    handleRecentFocusItemClick,
    handleGroupByChange
  };
};
